import "./TiptapEditor.scss";

import { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Color } from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import ListItem from "@tiptap/extension-list-item";
import Placeholder from "@tiptap/extension-placeholder";
import { isEqual } from "lodash";
import Link from "@tiptap/extension-link";
import { Button, Form, Modal } from "react-bootstrap";

const MenuBar = ({ editor, onLinkClick }) => {
  if (!editor) return null;

  return (
    <div className="control-group bg-light">
      <div className="button-group">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={editor.isActive("bold") ? "is-active" : ""}
          title="Bold"
        >
          <i className="fa fa-bold" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={editor.isActive("italic") ? "is-active" : ""}
          title="Italic"
        >
          <i className="fa fa-italic" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive("bulletList") ? "is-active" : ""}
          title="Bullet List"
        >
          <i className="fa fa-list-ul" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive("orderedList") ? "is-active" : ""}
          title="Ordered List"
        >
          <i className="fa fa-list-ol" />
        </button>

        <button
          onClick={onLinkClick}
          className={editor.isActive("link") ? "is-active" : ""}
          title="Insert Link"
        >
          <i className="fa fa-link" />
        </button>
      </div>
    </div>
  );
};

const TiptapEditor = ({ value = "", onChange, onEnter, placeholder, enableToolbar }) => {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  const editor = useEditor({
    extensions: [
      Color.configure({ types: [TextStyle.name, ListItem.name] }),
      TextStyle.configure({ types: [ListItem.name] }),
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || "Start typing...",
      }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
      }),
    ],
    content: value?.json || value || "",
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      const text = editor.getText();
      onChange?.({ json, text });
    },
    editorProps: {
      handleKeyDown(view, event) {
        const isEnter = event.key === "Enter";
        if (!isEnter) return false;
      
        const isModifier = event.ctrlKey || event.metaKey;
      
        // Plain Enter (no modifiers) → send
        if (!event.shiftKey && !isModifier) {
          event.preventDefault();
          typeof onEnter === "function" && onEnter();
          return true;
        }
      
        // Shift+Enter or Ctrl/Cmd+Enter inside a list
        const inList = editor.isActive("bulletList") || editor.isActive("orderedList");
        if ((event.shiftKey || isModifier) && inList) {
          const { $from } = view.state.selection;
          const parent = $from.parent; // paragraph
          const grand = $from.depth > 0 ? $from.node($from.depth - 1) : null; // listItem
      
          const isEmptyListItem =
            grand?.type?.name === "listItem" &&
            parent?.type?.name === "paragraph" &&
            parent.content.size === 0;
      
          event.preventDefault();
      
          if (isEmptyListItem) {
            // empty → break out of the list
            if (editor.chain().focus().splitListItem("listItem").run()) return true;
            if (editor.chain().focus().liftListItem("listItem").run()) return true;
            if (editor.chain().focus().insertContent("<p></p>").run()) return true;
            return false;
          }
      
          // non-empty → continue the list
          if (editor.chain().focus().splitListItem("listItem").run()) return true;
          return false;
        }
      
        // Shift+Enter or Ctrl/Cmd+Enter outside list
        if (event.shiftKey || isModifier) {
          const { $from } = view.state.selection;
          const parent = $from.parent;
      
          event.preventDefault();
      
          // if at the end of a paragraph → start a new paragraph block
          if (parent.type.name === "paragraph" && $from.parentOffset === parent.content.size) {
            if (editor.chain().focus().splitBlock().run()) return true;
          }
      
          // otherwise → insert a soft break
          if (editor.chain().focus().setHardBreak().run()) return true;
      
          return false;
        }
      
        return false;
      },
    },
  });

  // Sync external value into the editor if changed
  useEffect(() => {
    if (!editor) return;
  
    if (value?.json) {
      // When value is structured JSON, sync if changed
      if (!isEqual(value.json, editor.getJSON())) {
        editor.commands.setContent(value.json, false);
      }
    } else if (value === "" || value === null) {
      // Handle reset
      editor.commands.setContent("", false);
    }
  }, [value, editor]);

  return (
    <div className="message-editor">
      <Modal show={showLinkModal} onHide={() => setShowLinkModal(false)} centered className="FormPop">
        <Modal.Header closeButton>
          <Modal.Title>Insert Link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>URL</Form.Label>
            <Form.Control
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLinkModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run();
              setShowLinkModal(false);
            }}
            disabled={!linkUrl}
          >
            Insert
          </Button>
        </Modal.Footer>
      </Modal>
      {enableToolbar &&
        <MenuBar
          editor={editor}
          onLinkClick={() => {
            setLinkUrl(editor?.getAttributes("link")?.href || "");
            setShowLinkModal(true);
          }}
        />
      }
      <EditorContent
        editor={editor}
        className="tiptap"
        placeholder="Jot down"
      />
    </div>
  );
};

export default TiptapEditor;
